import cx from "classnames"
import { RenderBardWithComponents } from "components/Wysiwyg"
import { motion, useViewportScroll, useTransform } from "framer-motion"
import parse from "html-react-parser"
import { forwardRef, useEffect, useRef, useState } from "react"
import { DynamicHeadline } from "../DynamicHeadline"
import Section from "../Section"
import styles from "./Facts.module.scss"

const SCROLL_PADDING = 100 //approximation

const FactsTag = forwardRef(function FactsTag(
  { children, className, style, bigFacts },
  ref
) {
  return bigFacts ? (
    <motion.ul className={className} ref={ref} style={style}>
      {children}
    </motion.ul>
  ) : (
    <ul className={className} ref={ref}>
      {children}
    </ul>
  )
})

const Facts = ({ data = {}, colors }) => {
  const {
    dynamic_headline: dynamicHeadline,
    headline_type: headlineType,
    headline_appearance: headlineAppearance,
    facts_description: factsDescription,
    facts,
    ankerslug,
    big_facts,
  } = data

  const ref = useRef(null)
  const { scrollY } = useViewportScroll()

  const [moveX, setMoveX] = useState(0)
  const [scrollRange, setScrollRange] = useState([0, 0])

  useEffect(() => {
    const facts = ref.current
    if (!facts) return

    setMoveX(facts.offsetWidth - facts.scrollWidth)

    const rect = facts.getBoundingClientRect()
    const elementTop = rect.top + window.scrollY

    /**
     * scrollRange:
     *  start → element enters viewport
     *  end   → element reaches top of viewport
     */
    setScrollRange([
      elementTop - window.innerHeight + SCROLL_PADDING,
      elementTop - SCROLL_PADDING,
    ])
  }, [ref])

  const motionMovement = useTransform(scrollY, scrollRange, [0, moveX], {
    clamp: true,
  })

  return (
    <Section
      className={cx(styles.facts, {
        [styles.bigFacts]: big_facts,
      })}
      sectionSpacing={{
        top: data?.section_padding_top?.value || null,
        bottom: data?.section_padding_bottom?.value || null,
      }}
      id={ankerslug}
      colors={colors}
    >
      {(dynamicHeadline || factsDescription.length > 0) && (
        <div className={styles.factsHeader}>
          <DynamicHeadline
            headlineType={headlineType.value}
            headlineAppearance={headlineAppearance.value}
          >
            {dynamicHeadline}
          </DynamicHeadline>
          {factsDescription.length > 0 && (
            <div>
              <RenderBardWithComponents
                content={factsDescription}
                prefix="facts_description_"
              />
            </div>
          )}
        </div>
      )}
      <FactsTag
        ref={ref}
        className={styles.factsList}
        bigFacts={big_facts}
        style={{ x: motionMovement }}
      >
        {facts.map((fact, i) => (
          <li className={styles.fact} key={i}>
            <div className={styles.factHeadlineWrapper}>
              <div className={styles.factHeadlineGraphic} />
              <DynamicHeadline
                headlineType={fact.headline_type.value}
                headlineAppearance={fact.headline_appearance.value}
                className={styles.factHeadline}
              >
                {fact.dynamic_headline}
              </DynamicHeadline>
            </div>
            {fact.fact_description && (
              <div className={styles.text}>{parse(fact.fact_description)}</div>
            )}
          </li>
        ))}
      </FactsTag>
    </Section>
  )
}

export default Facts
